/* eslint-disable import/no-unused-modules */

/* eslint-disable no-restricted-imports */
import { useSubscribeToStatusUpdates } from "@/api";
import { AppDeploymentStatus } from "@/gql/__generated__/graphql";
import { useApolloClient } from "@apollo/client";
import {
  createContext,
  useState,
  ReactNode,
  useMemo,
  useCallback
} from "react";
import { useBackgroundTask } from "../features/organization-apps/hooks/useStartBackgroundCreateDeployApp";

type TaskContextType = {
  newAppID: string;
  isAppDisabled: boolean;
  taskStatus: AppDeploymentStatus | null;
  startBgTasks: (
    appID: string,
    owner: string,
    repo: string
  ) => Promise<void | null>;
  startTaskListener: (appID: string, deployVersionID: string) => void;
};

export const TaskContext = createContext<TaskContextType | undefined>(
  undefined
);

export const TaskProvider = ({ children }: { children: ReactNode }) => {
  const [newAppID, setNewAppID] = useState("");
  const [isAppDisabled, setIsAppDisabled] = useState(false);
  const [deployVID, setDeployVID] = useState("");
  const [taskStatus, setTaskStatus] = useState<AppDeploymentStatus | null>(
    null
  );

  const { startBackgroundTask } = useBackgroundTask();
  const client = useApolloClient();

  const startBgTasks = useCallback(
    async (appID: string, owner: string, repo: string) => {
      setIsAppDisabled(true);
      setNewAppID(appID);

      const deployVersionID = await startBackgroundTask(appID, owner, repo);

      if (deployVersionID) {
        setDeployVID(deployVersionID);
      }
    },
    [startBackgroundTask]
  );

  const startTaskListener: TaskContextType["startTaskListener"] = useCallback(
    (appID, deployVersionID) => {
      if (deployVersionID) {
        setIsAppDisabled(true);
        setNewAppID(appID);
        setDeployVID(deployVersionID);
      }
    },
    []
  );

  useSubscribeToStatusUpdates(deployVID, (status) => {
    setIsAppDisabled(false);
    setTaskStatus(status);
    if (status !== AppDeploymentStatus.Pending) {
      setDeployVID("");
    }
    if (status === AppDeploymentStatus.Running) {
      client.refetchQueries({
        include: ["GET_ORGANIZATION_APPS"]
      });
    }
  });

  const value = useMemo(
    () => ({
      newAppID,
      isAppDisabled,
      taskStatus,
      startBgTasks,
      startTaskListener
    }),
    [newAppID, isAppDisabled, taskStatus, startBgTasks, startTaskListener]
  );

  return <TaskContext.Provider value={value}>{children}</TaskContext.Provider>;
};
